import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Zap,
  Terminal,
  ArrowRight,
  Copy,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { ToolCall } from '../../types';

interface ToolTimelineProps {
  tools: ToolCall[];
  className?: string;
}

const statusConfig = {
  pending: {
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    icon: <Clock className="w-4 h-4" />,
  },
  running: {
    color: 'text-info',
    bg: 'bg-info/10',
    border: 'border-info/30',
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
  },
  success: {
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/30',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  error: {
    color: 'text-error',
    bg: 'bg-error/10',
    border: 'border-error/30',
    icon: <XCircle className="w-4 h-4" />,
  },
};

function ToolTimelineItem({ tool, index }: { tool: ToolCall; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const config = statusConfig[tool.status];

  const formatLatency = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatTime = (date: string): string => {
    return new Date(date).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const summaryParams = (params: Record<string, unknown>): string => {
    const keys = Object.keys(params);
    if (keys.length === 0) return 'no params';
    return keys.slice(0, 2).join(', ') + (keys.length > 2 ? ` +${keys.length - 2}` : '');
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 30 }}
      transition={{ delay: index * 0.08, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="relative"
    >
      {/* Timeline connector line */}
      <div className="absolute left-[15px] top-8 bottom-0 w-[2px] bg-white/5" />

      <div className="flex gap-3">
        {/* Status dot */}
        <motion.div
          className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10',
            config.bg,
            config.color
          )}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: index * 0.08 + 0.2, type: 'spring', stiffness: 500, damping: 25 }}
        >
          {config.icon}
        </motion.div>

        {/* Content */}
        <div className="flex-1 pb-4 min-w-0">
          <motion.button
            onClick={() => setExpanded(!expanded)}
            className={cn(
              'w-full text-left p-3 rounded-xl border transition-colors',
              'hover:bg-white/[0.03]',
              expanded ? 'bg-white/[0.03] border-white/10' : 'border-transparent'
            )}
            whileHover={{ x: 4 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header row */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Terminal className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
                <span className="font-mono text-sm text-text-primary truncate">
                  {tool.toolName}
                </span>
                <span className="text-xs text-text-muted flex-shrink-0">
                  ({summaryParams(tool.params)})
                </span>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Latency */}
                {tool.latency > 0 && (
                  <span
                    className={cn(
                      'text-[10px] font-mono px-1.5 py-0.5 rounded-full',
                      tool.latency < 500
                        ? 'bg-success/10 text-success'
                        : tool.latency < 2000
                        ? 'bg-warning/10 text-warning'
                        : 'bg-error/10 text-error'
                    )}
                  >
                    <Zap className="w-2.5 h-2.5 inline mr-0.5" />
                    {formatLatency(tool.latency)}
                  </span>
                )}

                {/* Expand chevron */}
                <motion.span
                  animate={{ rotate: expanded ? 90 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronRight className="w-4 h-4 text-text-muted" />
                </motion.span>
              </div>
            </div>

            {/* Timestamp and status */}
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] text-text-muted font-mono">
                {formatTime(tool.startTime)}
              </span>
              <ArrowRight className="w-2.5 h-2.5 text-text-muted" />
              <span
                className={cn(
                  'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                  config.bg,
                  config.color
                )}
              >
                {tool.status}
              </span>
              {tool.agentId && (
                <span className="text-[10px] text-text-muted">
                  via {tool.agentId}
                </span>
              )}
            </div>
          </motion.button>

          {/* Expanded details */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="overflow-hidden"
              >
                <div className="mt-2 ml-3 space-y-2">
                  {/* Parameters */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-text-muted font-medium">
                        Parameters
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(JSON.stringify(tool.params, null, 2));
                        }}
                        className="text-text-muted hover:text-text-primary transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <pre className="p-2.5 bg-black/30 rounded-lg text-[11px] text-text-secondary overflow-auto max-h-[120px] font-mono border border-white/5">
                      {JSON.stringify(tool.params, null, 2)}
                    </pre>
                  </div>

                  {/* Result */}
                  {tool.result && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wider text-success font-medium">
                          Result
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(JSON.stringify(tool.result, null, 2));
                          }}
                          className="text-text-muted hover:text-text-primary transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      <pre className="p-2.5 bg-success/5 rounded-lg text-[11px] text-success/90 overflow-auto max-h-[120px] font-mono border border-success/10">
                        {JSON.stringify(tool.result, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Error */}
                  {tool.error && (
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-error font-medium">
                        Error
                      </span>
                      <pre className="mt-1 p-2.5 bg-error/5 rounded-lg text-[11px] text-error overflow-auto max-h-[80px] font-mono border border-error/10">
                        {tool.error}
                      </pre>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

export function ToolTimeline({ tools, className }: ToolTimelineProps) {
  const [filter, setFilter] = useState<string>('all');

  const filteredTools =
    filter === 'all' ? tools : tools.filter((t) => t.status === filter);

  const statusCounts = {
    all: tools.length,
    pending: tools.filter((t) => t.status === 'pending').length,
    running: tools.filter((t) => t.status === 'running').length,
    success: tools.filter((t) => t.status === 'success').length,
    error: tools.filter((t) => t.status === 'error').length,
  };

  return (
    <div className={className}>
      {/* Filter tabs */}
      <div className="flex items-center gap-1 mb-4 p-1 bg-white/[0.03] rounded-lg">
        {(Object.keys(statusCounts) as Array<keyof typeof statusCounts>).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-all',
              filter === status
                ? 'bg-white/10 text-text-primary'
                : 'text-text-muted hover:text-text-secondary hover:bg-white/[0.02]'
            )}
          >
            <span className="capitalize">{status}</span>
            <span
              className={cn(
                'text-[10px] px-1.5 py-0.5 rounded-full',
                status === 'all' && 'bg-white/10',
                status === 'pending' && 'bg-warning/10 text-warning',
                status === 'running' && 'bg-info/10 text-info',
                status === 'success' && 'bg-success/10 text-success',
                status === 'error' && 'bg-error/10 text-error'
              )}
            >
              {statusCounts[status]}
            </span>
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
        <AnimatePresence mode="popLayout">
          {filteredTools.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 text-text-muted text-sm"
            >
              No tool calls to display
            </motion.div>
          ) : (
            filteredTools.map((tool, index) => (
              <ToolTimelineItem key={tool.id} tool={tool} index={index} />
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
